import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Calendar, Clock, MapPin, Users, Bell, Plus, Edit, Trash2, 
  Check, X, ChevronLeft, ChevronRight, Loader2, Mail, Gavel,
  Scale, FileQuestion, Building, Handshake, Tag, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Event type icons and colors
const eventTypeConfig = {
  court_date: { icon: Gavel, color: '#ef4444', name: 'Court Date' },
  hearing: { icon: Scale, color: '#f97316', name: 'Hearing' },
  deposition: { icon: FileQuestion, color: '#eab308', name: 'Deposition' },
  client_meeting: { icon: Users, color: '#22c55e', name: 'Client Meeting' },
  team_meeting: { icon: Users, color: '#3b82f6', name: 'Team Meeting' },
  deadline: { icon: Clock, color: '#dc2626', name: 'Deadline' },
  filing_due: { icon: AlertCircle, color: '#7c3aed', name: 'Filing Due' },
  mediation: { icon: Handshake, color: '#06b6d4', name: 'Mediation' },
  trial: { icon: Building, color: '#be185d', name: 'Trial' },
  custom: { icon: Tag, color: '#6366f1', name: 'Custom' },
  other: { icon: Calendar, color: '#6b7280', name: 'Other' }
};

// Reminder presets
const reminderPresets = [
  { label: '15 minutes before', time: 15, unit: 'minutes' },
  { label: '1 hour before', time: 1, unit: 'hours' },
  { label: '1 day before', time: 1, unit: 'days' },
  { label: '3 days before', time: 3, unit: 'days' },
  { label: '1 week before', time: 1, unit: 'weeks' }
];

export default function CaseEventsTab({ caseId, caseData }) {
  const { token } = useAuth();
  
  // State
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState({ system_categories: [], custom_categories: [] });
  const [teamMembers, setTeamMembers] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  
  // Dialog state
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // View state
  const [viewMode, setViewMode] = useState('list'); // list, calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Form state
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'other',
    custom_category_id: '',
    start_datetime: '',
    end_datetime: '',
    all_day: false,
    location: '',
    attendee_user_ids: [],
    notify_client: false,
    client_notification_message: '',
    reminders: [],
    sync_to_calendar: true,
    add_to_firm_calendar: false
  });

  // Fetch data
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/events`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setEvents(await res.json());
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [caseId, token]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/event-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [token]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/team/members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data.members || []);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [token]);

  const fetchCalendarStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/calendar/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCalendarStatus(await res.json());
      }
    } catch (error) {
      console.error('Error fetching calendar status:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchEvents(),
        fetchCategories(),
        fetchTeamMembers(),
        fetchCalendarStatus()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchEvents, fetchCategories, fetchTeamMembers, fetchCalendarStatus]);

  // Reset form
  const resetForm = () => {
    const now = new Date();
    const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
    
    setEventForm({
      title: '',
      description: '',
      event_type: 'other',
      custom_category_id: '',
      start_datetime: now.toISOString().slice(0, 16),
      end_datetime: oneHourLater.toISOString().slice(0, 16),
      all_day: false,
      location: '',
      attendee_user_ids: [],
      notify_client: false,
      client_notification_message: '',
      reminders: [{ enabled: true, time_before: 1, unit: 'days', notify_email: true, notify_app: true }],
      sync_to_calendar: true,
      add_to_firm_calendar: false
    });
    setEditingEvent(null);
  };

  // Open create dialog
  const handleCreate = () => {
    resetForm();
    setShowEventDialog(true);
  };

  // Open edit dialog
  const handleEdit = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      custom_category_id: event.custom_category_id || '',
      start_datetime: event.start_datetime.slice(0, 16),
      end_datetime: event.end_datetime.slice(0, 16),
      all_day: event.all_day || false,
      location: event.location || '',
      attendee_user_ids: event.attendees?.map(a => a.user_id) || [],
      notify_client: event.notify_client || false,
      client_notification_message: event.client_notification_message || '',
      reminders: event.reminders || [],
      sync_to_calendar: event.sync_to_calendar ?? true,
      add_to_firm_calendar: event.add_to_firm_calendar || false
    });
    setShowEventDialog(true);
  };

  // Save event
  const handleSave = async () => {
    if (!eventForm.title || !eventForm.start_datetime || !eventForm.end_datetime) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', eventForm.title);
      formData.append('description', eventForm.description);
      formData.append('event_type', eventForm.event_type);
      formData.append('custom_category_id', eventForm.custom_category_id);
      formData.append('start_datetime', new Date(eventForm.start_datetime).toISOString());
      formData.append('end_datetime', new Date(eventForm.end_datetime).toISOString());
      formData.append('all_day', eventForm.all_day);
      formData.append('location', eventForm.location);
      formData.append('attendee_user_ids', JSON.stringify(eventForm.attendee_user_ids));
      formData.append('notify_client', eventForm.notify_client);
      formData.append('client_notification_message', eventForm.client_notification_message);
      formData.append('reminders', JSON.stringify(eventForm.reminders));
      formData.append('sync_to_calendar', eventForm.sync_to_calendar);
      formData.append('add_to_firm_calendar', eventForm.add_to_firm_calendar);

      const url = editingEvent 
        ? `${API_URL}/api/case-management/cases/${caseId}/events/${editingEvent.id}`
        : `${API_URL}/api/case-management/cases/${caseId}/events`;
      
      const res = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(editingEvent ? 'Event updated' : 'Event created');
        
        // Show calendar sync status
        if (data.calendar_sync?.created) {
          toast.success('Event added to your calendar');
        } else if (eventForm.sync_to_calendar && !calendarStatus.connected) {
          toast.info('Connect your calendar in Firm Settings to sync events');
        }
        
        setShowEventDialog(false);
        fetchEvents();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save event');
      }
    } catch (error) {
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  // Delete event
  const handleDelete = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Event deleted');
        fetchEvents();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Add reminder
  const addReminder = () => {
    setEventForm(prev => ({
      ...prev,
      reminders: [...prev.reminders, { enabled: true, time_before: 1, unit: 'days', notify_email: true, notify_app: true }]
    }));
  };

  // Remove reminder
  const removeReminder = (index) => {
    setEventForm(prev => ({
      ...prev,
      reminders: prev.reminders.filter((_, i) => i !== index)
    }));
  };

  // Update reminder
  const updateReminder = (index, field, value) => {
    setEventForm(prev => ({
      ...prev,
      reminders: prev.reminders.map((r, i) => i === index ? { ...r, [field]: value } : r)
    }));
  };

  // Toggle attendee
  const toggleAttendee = (userId) => {
    setEventForm(prev => ({
      ...prev,
      attendee_user_ids: prev.attendee_user_ids.includes(userId)
        ? prev.attendee_user_ids.filter(id => id !== userId)
        : [...prev.attendee_user_ids, userId]
    }));
  };

  // Get event color
  const getEventColor = (event) => {
    if (event.custom_category_id && event.category_color) {
      return event.category_color;
    }
    return eventTypeConfig[event.event_type]?.color || '#6b7280';
  };

  // Get event icon
  const getEventIcon = (event) => {
    if (event.event_type === 'custom') {
      return Tag;
    }
    return eventTypeConfig[event.event_type]?.icon || Calendar;
  };

  // Format datetime for display
  const formatDateTime = (datetime, allDay) => {
    const date = new Date(datetime);
    if (allDay) {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  // Check if event is upcoming
  const isUpcoming = (datetime) => new Date(datetime) > new Date();
  const isPast = (datetime) => new Date(datetime) < new Date();

  // Get all categories
  const allCategories = [...categories.system_categories, ...categories.custom_categories];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-events-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Events & Calendar</h2>
          <p className="text-sm text-slate-500">
            {events.length} event{events.length !== 1 ? 's' : ''} • 
            {calendarStatus.connected ? (
              <span className="text-emerald-600 ml-1">
                <Check className="w-3 h-3 inline" /> Calendar connected ({calendarStatus.provider})
              </span>
            ) : (
              <span className="text-amber-600 ml-1">Calendar not connected</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}>
            <Calendar className="w-4 h-4 mr-1" />
            {viewMode === 'list' ? 'Calendar View' : 'List View'}
          </Button>
          <Button onClick={handleCreate} data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add Event
          </Button>
        </div>
      </div>

      {/* Events List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {events.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-700">No events scheduled</h3>
                <p className="text-sm text-slate-500 mt-1 mb-4">
                  Add court dates, meetings, deadlines, and other important events
                </p>
                <Button onClick={handleCreate}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add First Event
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Upcoming Events */}
              {events.filter(e => isUpcoming(e.start_datetime)).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-500 mb-3">Upcoming</h3>
                  <div className="space-y-2">
                    {events
                      .filter(e => isUpcoming(e.start_datetime))
                      .map(event => {
                        const EventIcon = getEventIcon(event);
                        const color = getEventColor(event);
                        return (
                          <Card key={event.id} className="hover:shadow-md transition-shadow" data-testid={`event-${event.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <div 
                                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${color}20` }}
                                >
                                  <EventIcon className="w-5 h-5" style={{ color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <h4 className="font-medium text-slate-900">{event.title}</h4>
                                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3.5 h-3.5" />
                                          {formatDateTime(event.start_datetime, event.all_day)}
                                        </span>
                                        {event.location && (
                                          <span className="flex items-center gap-1">
                                            <MapPin className="w-3.5 h-3.5" />
                                            {event.location}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Badge style={{ backgroundColor: `${color}20`, color }}>{event.category_name || eventTypeConfig[event.event_type]?.name}</Badge>
                                      <Button variant="ghost" size="sm" onClick={() => handleEdit(event)}>
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => handleDelete(event.id)} className="text-red-600 hover:text-red-700">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  {event.description && (
                                    <p className="text-sm text-slate-600 mt-2">{event.description}</p>
                                  )}
                                  {event.attendees?.length > 0 && (
                                    <div className="flex items-center gap-1 mt-2">
                                      <Users className="w-3.5 h-3.5 text-slate-400" />
                                      <span className="text-xs text-slate-500">
                                        {event.attendees.map(a => a.name).join(', ')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Past Events */}
              {events.filter(e => isPast(e.start_datetime)).length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-500 mb-3">Past</h3>
                  <div className="space-y-2 opacity-75">
                    {events
                      .filter(e => isPast(e.start_datetime))
                      .map(event => {
                        const EventIcon = getEventIcon(event);
                        const color = getEventColor(event);
                        return (
                          <Card key={event.id} className="bg-slate-50" data-testid={`event-${event.id}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-4">
                                <div 
                                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${color}15` }}
                                >
                                  <EventIcon className="w-5 h-5" style={{ color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <h4 className="font-medium text-slate-700">{event.title}</h4>
                                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3.5 h-3.5" />
                                          {formatDateTime(event.start_datetime, event.all_day)}
                                        </span>
                                      </div>
                                    </div>
                                    <Badge variant="outline">{event.category_name || eventTypeConfig[event.event_type]?.name}</Badge>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="font-semibold">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Calendar grid would go here - simplified for now */}
            <div className="text-center text-sm text-slate-500 py-8">
              Calendar view coming soon. Use list view for now.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update event details' : 'Add a new event to this case'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                value={eventForm.title}
                onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Deposition of John Smith"
                data-testid="event-title-input"
              />
            </div>

            {/* Event Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event_type">Event Type</Label>
                <Select
                  value={eventForm.event_type}
                  onValueChange={(v) => setEventForm(prev => ({ ...prev, event_type: v, custom_category_id: '' }))}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.system_categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </span>
                      </SelectItem>
                    ))}
                    {categories.custom_categories.length > 0 && (
                      <SelectItem value="custom">Custom Category</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Category */}
              {eventForm.event_type === 'custom' && categories.custom_categories.length > 0 && (
                <div>
                  <Label htmlFor="custom_category">Custom Category</Label>
                  <Select
                    value={eventForm.custom_category_id}
                    onValueChange={(v) => setEventForm(prev => ({ ...prev, custom_category_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.custom_categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                            {cat.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Date & Time */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="all_day"
                  checked={eventForm.all_day}
                  onCheckedChange={(checked) => setEventForm(prev => ({ ...prev, all_day: checked }))}
                />
                <Label htmlFor="all_day" className="cursor-pointer">All day event</Label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_datetime">Start *</Label>
                  <Input
                    id="start_datetime"
                    type={eventForm.all_day ? 'date' : 'datetime-local'}
                    value={eventForm.all_day ? eventForm.start_datetime.slice(0, 10) : eventForm.start_datetime}
                    onChange={(e) => setEventForm(prev => ({ ...prev, start_datetime: e.target.value }))}
                    data-testid="event-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="end_datetime">End *</Label>
                  <Input
                    id="end_datetime"
                    type={eventForm.all_day ? 'date' : 'datetime-local'}
                    value={eventForm.all_day ? eventForm.end_datetime.slice(0, 10) : eventForm.end_datetime}
                    onChange={(e) => setEventForm(prev => ({ ...prev, end_datetime: e.target.value }))}
                    data-testid="event-end-input"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={eventForm.location}
                onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Courthouse Room 302"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={eventForm.description}
                onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add any notes or details..."
                rows={3}
              />
            </div>

            {/* Attendees */}
            {teamMembers.length > 0 && (
              <div>
                <Label>Notify Team Members</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 p-3 border rounded-lg max-h-32 overflow-y-auto">
                  {teamMembers.map(member => (
                    <div key={member.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`attendee-${member.id}`}
                        checked={eventForm.attendee_user_ids.includes(member.id)}
                        onCheckedChange={() => toggleAttendee(member.id)}
                      />
                      <Label htmlFor={`attendee-${member.id}`} className="cursor-pointer text-sm">
                        {member.name || member.email}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Client Notification */}
            {caseData?.client_email && (
              <div className="space-y-2 p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="notify_client"
                    checked={eventForm.notify_client}
                    onCheckedChange={(checked) => setEventForm(prev => ({ ...prev, notify_client: checked }))}
                  />
                  <Label htmlFor="notify_client" className="cursor-pointer flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Send email notification to client ({caseData.client_email})
                  </Label>
                </div>
                {eventForm.notify_client && (
                  <Textarea
                    value={eventForm.client_notification_message}
                    onChange={(e) => setEventForm(prev => ({ ...prev, client_notification_message: e.target.value }))}
                    placeholder="Optional: Add a custom message for the client..."
                    rows={2}
                    className="mt-2"
                  />
                )}
              </div>
            )}

            {/* Reminders */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Reminders</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addReminder}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Reminder
                </Button>
              </div>
              {eventForm.reminders.length === 0 ? (
                <p className="text-sm text-slate-500">No reminders set</p>
              ) : (
                <div className="space-y-2">
                  {eventForm.reminders.map((reminder, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                      <Input
                        type="number"
                        min="1"
                        value={reminder.time_before}
                        onChange={(e) => updateReminder(index, 'time_before', parseInt(e.target.value))}
                        className="w-20"
                      />
                      <Select
                        value={reminder.unit}
                        onValueChange={(v) => updateReminder(index, 'unit', v)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">minutes</SelectItem>
                          <SelectItem value="hours">hours</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                          <SelectItem value="weeks">weeks</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-slate-500">before</span>
                      <div className="flex-1" />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeReminder(index)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Calendar Sync */}
            <div className="space-y-2 p-3 border rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="sync_to_calendar"
                  checked={eventForm.sync_to_calendar}
                  onCheckedChange={(checked) => setEventForm(prev => ({ ...prev, sync_to_calendar: checked }))}
                />
                <Label htmlFor="sync_to_calendar" className="cursor-pointer">
                  Sync to my calendar
                  {calendarStatus.connected ? (
                    <span className="text-emerald-600 text-xs ml-2">({calendarStatus.provider})</span>
                  ) : (
                    <span className="text-amber-600 text-xs ml-2">(not connected)</span>
                  )}
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="add_to_firm_calendar"
                  checked={eventForm.add_to_firm_calendar}
                  onCheckedChange={(checked) => setEventForm(prev => ({ ...prev, add_to_firm_calendar: checked }))}
                />
                <Label htmlFor="add_to_firm_calendar" className="cursor-pointer">
                  Also add to firm shared calendar
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                editingEvent ? 'Update Event' : 'Create Event'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
